import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons'
import { useAccountContext } from '../../../contexts/AccountContext'
import { useAuth } from '../../../hooks/useAuth'
import { useDaySequenceDetail, useDeviceMissingReadingsSeries } from '../../../hooks/admin/useMissingReadings'
import { useDevice } from '../../../hooks/admin/useDevices'
import { useUrlBoolean, useUrlNumber, useUrlDayRange } from '../../../hooks/useUrlState'
import { formatRicLabel } from '../../../utils/ricLabel'
import DayStepper, { visibleWindow } from '../../../components/admin/missingReadings/DayStepper'
import { LabeledSwitch, MISSING_READINGS_MAX_DAYS } from '../../../components/admin/missingReadings/controls'
import PeriodSummary from '../../../components/admin/missingReadings/PeriodSummary'
import type { DaySequenceGap, DaySequenceMessage } from '../../../types/missingReadings'

/**
 * One device, one day, message by message.
 *
 * The bottom rung of the drill-in, and the only view that names individual
 * transmissions rather than counting them. Every message the device sent that
 * day is listed with its Sigfox sequence number, and every sequence number that
 * should have sat between two of them and never arrived is reported as a gap.
 *
 * Read the two "expected" figures carefully, because they are not the same
 * number and the page deliberately keeps them apart:
 *
 *  - The SEQUENCE SPAN is derived from the data — first sequence number
 *    received that day through the last, walked forward with the 4096 wrap.
 *    Nothing in the data says what number the device should have started the
 *    day on, so a device that was silent until noon shows a short, complete
 *    span rather than a half-empty one. Gaps at the edges of a day are
 *    invisible here by construction.
 *  - The READING ESTIMATE comes from the device's configured SCHD interval and
 *    is the signal that does catch an edge-of-day silence. It is withheld
 *    entirely for a day the device was reconfigured.
 */

const UTC_TIME = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
  timeZone: 'UTC',
})

const timeOf = (iso: string) => UTC_TIME.format(new Date(iso))

/** "4h 12m", "12m 30s", "45s" — enough precision to tell an outage from a blip. */
function describeSilence(fromIso: string, toIso: string): string {
  const ms = new Date(toIso).getTime() - new Date(fromIso).getTime()
  if (!Number.isFinite(ms) || ms <= 0) return '—'
  const totalSeconds = Math.round(ms / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  if (hours > 0) return `${hours}h ${minutes}m`
  if (minutes > 0) return `${minutes}m ${seconds}s`
  return `${seconds}s`
}

/** A gap's missing numbers as a range, since they are contiguous by construction. */
function describeRange(gap: DaySequenceGap): string {
  if (gap.missing.length === 0) return '—'
  const first = gap.missing[0]
  const last = gap.missing[gap.missing.length - 1]
  return first === last ? `${first}` : `${first}–${last}`
}

/**
 * Cap on the explicit sequence numbers rendered for one gap.
 *
 * A device silent for a whole day can miss most of the 4096-wide counter, and
 * the API returns every one of those numbers. Listing them all would put
 * thousands of nodes on the page to say what the range already said.
 */
const MISSING_LIST_CAP = 200

/**
 * How many missing sequence numbers a gap may spell out, one row each, before
 * the log collapses it to a single summary row.
 *
 * A gap can be hundreds of numbers wide. Enumerating one of those would bury
 * the messages that actually arrived under rows for messages that did not,
 * which is the opposite of what the log is for.
 */
const GAP_ROW_CAP = 5

/**
 * One line of the message log: either a message that arrived, or a hole where
 * one should have been. The holes are rendered, not merely implied by a jump in
 * the numbers, because spotting that jump is work the page can do for you.
 */
type LogRow =
  | { kind: 'message'; message: DaySequenceMessage }
  | { kind: 'missing'; seq: number; gap: number; first: boolean }
  | { kind: 'missing-range'; from: number; to: number; count: number; gap: number; first: boolean }

/** Placeholder rows for one gap — spelled out, or collapsed when it is wide. */
function rowsForGap(gap: DaySequenceGap, index: number, expandAll = false): LogRow[] {
  if (gap.missing.length === 0) return []
  if (expandAll || gap.missing.length <= GAP_ROW_CAP) {
    return gap.missing.map((seq, i) => ({ kind: 'missing', seq, gap: index, first: i === 0 }))
  }
  return [
    {
      kind: 'missing-range',
      from: gap.missing[0],
      to: gap.missing[gap.missing.length - 1],
      count: gap.count,
      gap: index,
      first: true,
    },
  ]
}

function SummaryCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="text-xs font-medium text-gray-500 uppercase">{label}</div>
      <div className="text-lg font-semibold text-gray-900 mt-1">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
    </div>
  )
}

/**
 * One labelled fact about the device, for the row under the title.
 *
 * Rendered only when there is a value: a blank slot in this row is noise, and
 * the fields come from device records that are filled in to varying degrees.
 */
function DetailItem({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null
  return (
    <div>
      <dt className="text-xs font-medium text-gray-500 uppercase">{label}</dt>
      <dd className="text-sm text-gray-900">{value}</dd>
    </div>
  )
}

export default function DeviceMissingReadingsDayPage() {
  const { date, deviceId: deviceIdParam } = useParams<{ date: string; deviceId: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { devices } = useAccountContext()
  const { isAdmin } = useAuth()
  const admin = isAdmin()

  const day = date ?? ''
  const deviceId = deviceIdParam ? Number(deviceIdParam) : null
  const validDeviceId = deviceId !== null && Number.isInteger(deviceId) && deviceId > 0 ? deviceId : null

  /**
   * Follow the device selector in the left nav.
   *
   * Selecting a device anywhere in the app appends ?sigfoxId= to whatever URL
   * is open, which on this page would otherwise leave the sidebar highlighting
   * one device while the page still showed another. The path carries the
   * internal DEVICE_ID, so the sigfoxId is resolved against the account
   * context and the path is rewritten to match.
   *
   * A device the context does not know about (outside the caller's accounts)
   * is left alone rather than guessed at.
   */
  const selectedSigfoxId = searchParams.get('sigfoxId')
  useEffect(() => {
    if (!selectedSigfoxId) return
    const selected = (devices as Record<string, { deviceId: number }>)[selectedSigfoxId]
    if (!selected || selected.deviceId === validDeviceId) return
    const params = new URLSearchParams(searchParams)
    navigate(`/missing-readings/${day}/${selected.deviceId}?${params.toString()}`, { replace: true })
  }, [selectedSigfoxId, devices, validDeviceId, day, navigate, searchParams])

  const { data, isLoading, isError, error } = useDaySequenceDetail(validDeviceId, day || null, admin)
  // The device record, for the identity row under the title. A diagnostic that
  // names a unit only by its Sigfox ID asks the reader to go and look up which
  // RIC that is before they can act on it — the field name belongs here.
  const { data: device } = useDevice(admin && validDeviceId ? validDeviceId : 0)
  // Doubles as the stepper's markers and as the source of this day's interval
  // estimate, which the sequence endpoint deliberately does not carry.
  const [rangeDays, setRangeDays] = useUrlNumber('range', 90)
  const [zoom, setZoom] = useUrlDayRange('zoom')
  // Off by default: a wide gap collapses to one line so hundreds of
  // placeholders don't bury the day's real traffic. On, every missing sequence
  // number gets its own row. In the URL so a refresh keeps the expanded view.
  const [showAllMissing, setShowAllMissing] = useUrlBoolean('allMissing', false)
  // Transient, so deliberately not in the URL: it only lives while a stat in
  // the period summary is being hovered.
  const [highlightDays, setHighlightDays] = useState<Set<string> | null>(null)
  const { data: series } = useDeviceMissingReadingsSeries(validDeviceId, rangeDays, admin)

  const [expanded, setExpanded] = useState<number | null>(null)
  // The caveat about how gaps are counted matters the first time and is noise
  // every time after, and on this viewport-fitted page every line of it is a
  // line the message log does not get. Closed by default, one click to read.
  // Null until the reader toggles it; until then the open state follows
  // whether this day actually needs the warning (see readingsShortfall).
  const [gapHelpOverride, setGapHelpOverride] = useState<boolean | null>(null)
  // A new day gets its own default rather than inheriting the last day's toggle.
  useEffect(() => setGapHelpOverride(null), [day])

  /**
   * Gap table <-> message log. The table says a gap exists; the log shows it in
   * place among the messages either side. Hovering a gap row lights its
   * placeholder rows in the log; clicking scrolls the log to it and flashes it,
   * since the gap is usually far below the fold of a scrolling log.
   *
   * Hover alone deliberately does not scroll — sweeping the mouse down the
   * table would otherwise yank the log around under the reader.
   */
  const [hoveredGap, setHoveredGap] = useState<number | null>(null)
  const [flashedGap, setFlashedGap] = useState<number | null>(null)
  const gapRowRefs = useRef<Record<number, HTMLTableRowElement | null>>({})
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const revealGap = (index: number) => {
    gapRowRefs.current[index]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setFlashedGap(index)
    if (flashTimer.current) clearTimeout(flashTimer.current)
    flashTimer.current = setTimeout(() => setFlashedGap(null), 1600)
  }
  useEffect(
    () => () => {
      if (flashTimer.current) clearTimeout(flashTimer.current)
    },
    []
  )

  const seriesRow = useMemo(() => (series?.series ?? []).find((row) => row.day === day) ?? null, [series, day])

  /**
   * The edge-silence caveat is only noise on a day whose readings add up. It
   * becomes the most important sentence on the page when they don't: fewer
   * readings than the interval predicts, with gap detection blind to silence
   * before the first message or after the last, is exactly when the Missed
   * tile can read low and mislead. So it opens itself on those days.
   */
  const readingsShortfall =
    !!seriesRow &&
    !seriesRow.reconfigured &&
    seriesRow.expectedReadings !== null &&
    seriesRow.receivedReadings < seriesRow.expectedReadings
  const showGapHelp = gapHelpOverride ?? readingsShortfall

  /**
   * The messages that arrived, with each gap's missing numbers spliced in where
   * they belong. Gaps come back in time order and each one names the message it
   * follows, so a single pointer walks both lists together.
   */
  const logRows = useMemo<LogRow[]>(() => {
    const messages = data?.messages ?? []
    const gaps = data?.gaps ?? []
    const rows: LogRow[] = []
    let next = 0
    for (const message of messages) {
      rows.push({ kind: 'message', message })
      while (next < gaps.length && gaps[next].after === message.seq) {
        rows.push(...rowsForGap(gaps[next], next, showAllMissing))
        next += 1
      }
    }
    return rows
  }, [data, showAllMissing])

  const goToDay = (next: string) => navigate(`/missing-readings/${next}/${deviceIdParam}?${searchParams.toString()}`)

  const sigfoxId = data?.sigfoxId ?? device?.sigfoxId ?? null
  const ricLabel = formatRicLabel(device?.serialNumber)
  // The field name wins the title when we have it; the Sigfox ID keeps the
  // heading from going blank while the device record is still loading.
  const title = ricLabel ?? sigfoxId ?? (validDeviceId ? `Device ${validDeviceId}` : 'Device')

  const header = (
    <div className="mb-4">
      <div className="flex items-center gap-1 text-sm">
        <Link to={`/missing-readings/${day}`} className="text-blue-600 hover:underline">
          ‹ All devices on {day}
        </Link>
        <span className="text-gray-300 px-1">·</span>
        <Link to="/dashboard" className="text-blue-600 hover:underline">
          Dashboard
        </Link>
      </div>
      <div className="flex flex-wrap items-baseline gap-x-3 mt-2">
        <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        {ricLabel && sigfoxId && <span className="text-sm text-gray-500">Sigfox {sigfoxId}</span>}
      </div>
      <p className="text-sm text-gray-600 mt-1">Sequence detail for {day} (UTC)</p>
      {device && (
        <dl className="mt-3 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-x-6 gap-y-2">
          <DetailItem label="Site" value={device.siteName} />
          <DetailItem label="Group" value={device.locationName} />
          <DetailItem label="Customer" value={device.customerName} />
          <DetailItem label="Partner" value={device.partnerName} />
          <DetailItem label="Hardware" value={device.hardwareVersion} />
          <DetailItem label="Date code" value={device.dateCode} />
        </dl>
      )}
    </div>
  )

  if (!validDeviceId) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        {header}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-10 text-center">
          <p className="text-gray-900 font-medium">That is not a device</p>
          <p className="text-sm text-gray-600 mt-1">The device id in the address is not a valid number.</p>
        </div>
      </div>
    )
  }

  if (isError) {
    const code = (error as any)?.response?.data?.error?.code
    const notFound = code === 'DEVICE_NOT_FOUND'
    const badDate = code === 'INVALID_DATE'
    return (
      <div className="p-6 max-w-3xl mx-auto">
        {header}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-10 text-center">
          <p className="text-gray-900 font-medium">
            {notFound ? 'Device not found' : badDate ? 'That day is outside the window' : 'Could not load this day'}
          </p>
          <p className="text-sm text-gray-600 mt-1">
            {notFound
              ? 'No device with that id exists. It may have been removed.'
              : badDate
              ? `Missing-readings history goes back ${MISSING_READINGS_MAX_DAYS} days and does not include the future.`
              : (error as any)?.response?.data?.error?.message || 'Please try again.'}
          </p>
        </div>
      </div>
    )
  }

  return (
    // The message log is the point of this page, so on a tall enough viewport
    // the page takes exactly the height the chrome leaves it (fixed 64px nav
    // plus the layout's 24px band top and bottom) and lets the log take
    // whatever is left. Everything above it is fixed-height, so the page never
    // grows a scrollbar of its own.
    <div className="p-6 max-w-7xl mx-auto flex flex-col lg:h-[calc(100vh-7rem)] lg:min-h-0">
      {header}

      <div className="mb-4">
        <DayStepper
          date={day}
          onChange={goToDay}
          rows={series?.series ?? []}
          rangeDays={rangeDays}
          onRangeChange={setRangeDays}
          zoom={zoom}
          onZoomChange={setZoom}
          highlightDays={highlightDays}
        />
        {series && (
          <div className="mt-2">
            <PeriodSummary
              rows={series.series}
              {...visibleWindow(rangeDays, MISSING_READINGS_MAX_DAYS, zoom)}
              zoomed={!!zoom}
              onSelectDay={goToDay}
              onHighlight={(days) => setHighlightDays(days ? new Set(days) : null)}
            />
          </div>
        )}
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : !data ? (
        <p className="text-sm text-gray-500">No detail available for this day.</p>
      ) : data.messages.length === 0 ? (
        // Silence for a whole day is itself the finding, and a louder one than
        // any gap count — say so rather than showing an empty table.
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-10 text-center">
          <p className="text-gray-900 font-medium">No messages received from this device on {day}</p>
          <p className="text-sm text-gray-600 mt-1">
            The device sent nothing at all, so there are no sequence numbers to compare.
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-1.5">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Selected day <span className="normal-case tracking-normal font-normal text-gray-400">· {day}</span>
            </h2>
            <button
              type="button"
              onClick={() => setGapHelpOverride(!showGapHelp)}
              aria-expanded={showGapHelp}
              aria-controls="gap-help"
              className={`inline-flex items-center gap-1 text-xs ${
                showGapHelp ? 'text-blue-600' : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <FontAwesomeIcon icon={faCircleInfo} className="h-3 w-3" />
              How gaps are counted
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <SummaryCard label="Received" value={data.summary.received.toLocaleString()} sub="messages, all types" />
            <SummaryCard
              label="Missed"
              value={data.summary.missed.toLocaleString()}
              sub={`${data.gaps.length} gap${data.gaps.length === 1 ? '' : 's'}`}
            />
            <SummaryCard
              label="Sequence span"
              value={data.summary.firstSeq === null ? '—' : `${data.summary.firstSeq}–${data.summary.lastSeq}`}
              sub={`${data.summary.expected.toLocaleString()} numbers, first to last`}
            />
            <SummaryCard
              label="Readings"
              value={
                seriesRow
                  ? `${seriesRow.receivedReadings.toLocaleString()} of ${
                      seriesRow.expectedReadings?.toLocaleString() ?? '—'
                    }`
                  : '—'
              }
              sub={seriesRow?.reconfigured ? '* reconfigured — no estimate' : 'received of interval estimate'}
            />
          </div>

          {showGapHelp ? (
            <p
              id="gap-help"
              className="text-xs text-gray-600 mb-4 rounded-md bg-blue-50 border border-blue-100 px-3 py-2"
            >
              Gaps are found <em>between</em> messages that arrived, so a device that went silent before its first
              message or after its last shows no gap for that silence. The Readings tile is what catches those — it
              compares the day against the device's configured interval.
              {gapHelpOverride === null && readingsShortfall && (
                <span className="block mt-1 text-blue-700">
                  Shown because this day received fewer readings than its interval estimate.
                </span>
              )}
            </p>
          ) : (
            <div className="mb-4" />
          )}

          <h2 className="text-sm font-semibold text-gray-700 mb-2">
            Missing sequence numbers ({data.summary.missed.toLocaleString()})
          </h2>
          {data.gaps.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center mb-6">
              <p className="text-gray-900 font-medium">No gaps on {day}</p>
              <p className="text-sm text-gray-600 mt-1">
                Every message between {data.summary.firstSeq} and {data.summary.lastSeq} arrived.
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6 lg:max-h-64 lg:overflow-y-auto lg:shrink-0">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 lg:sticky lg:top-0">
                  <tr className="text-gray-600">
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase">Sequence</th>
                    <th className="px-4 py-2 text-right text-xs font-medium uppercase">Count</th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase">Between</th>
                    <th className="px-4 py-2 text-right text-xs font-medium uppercase">Silent for</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.gaps.map((gap, index) => (
                    <tr
                      key={`${gap.after}-${gap.before}-${index}`}
                      onMouseEnter={() => setHoveredGap(index)}
                      onMouseLeave={() => setHoveredGap(null)}
                      onClick={() => revealGap(index)}
                      onFocus={() => setHoveredGap(index)}
                      onBlur={() => setHoveredGap(null)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          revealGap(index)
                        }
                      }}
                      tabIndex={0}
                      title="Show this gap in the message log"
                      className={`align-top cursor-pointer ${hoveredGap === index ? 'bg-red-50' : 'hover:bg-red-50'}`}
                    >
                      <td className="px-4 py-2 tabular-nums text-gray-900 font-medium">
                        {describeRange(gap)}
                        {expanded === index && (
                          <div className="mt-1 text-xs text-gray-500 font-normal tabular-nums break-words max-w-md">
                            {gap.missing.slice(0, MISSING_LIST_CAP).join(', ')}
                            {gap.missing.length > MISSING_LIST_CAP &&
                              ` … +${(gap.missing.length - MISSING_LIST_CAP).toLocaleString()} more`}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-red-600 font-semibold">
                        {gap.count.toLocaleString()}
                      </td>
                      <td className="px-4 py-2 text-gray-600 tabular-nums whitespace-nowrap">
                        {timeOf(gap.fromAt)} → {timeOf(gap.toAt)}
                        <span className="text-gray-400 ml-2">
                          (seq {gap.after} → {gap.before})
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-gray-600 whitespace-nowrap">
                        {describeSilence(gap.fromAt, gap.toAt)}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            setExpanded(expanded === index ? null : index)
                          }}
                          className="text-xs text-blue-600 hover:underline whitespace-nowrap"
                        >
                          {expanded === index ? 'Hide' : 'List'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-between gap-3 mb-2">
            <h2 className="text-sm font-semibold text-gray-700">
              Received messages ({data.summary.received.toLocaleString()})
            </h2>
            {data.gaps.some((g) => g.missing.length > GAP_ROW_CAP) && (
              <LabeledSwitch
                checked={showAllMissing}
                onCheckedChange={setShowAllMissing}
                label="Show every missing message"
              />
            )}
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden lg:flex-1 lg:min-h-0 lg:flex lg:flex-col">
            <div className="max-h-96 overflow-y-auto lg:max-h-none lg:flex-1 lg:min-h-0">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr className="text-gray-600">
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase">Sigfox seq num</th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase">Time (UTC)</th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase">Type</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {logRows.map((row, index) => {
                    // Placeholders are loud on purpose. A quiet grey row reads
                    // as absent but scrolls straight past the eye, and finding
                    // the holes is the whole job here — so the missing rows are
                    // the brightest thing in the log, louder than the pale tint
                    // on the real messages either side. MISSING in the type
                    // column carries the same message without relying on
                    // colour, for greyscale and colour-blind readers.
                    if (row.kind === 'missing') {
                      return (
                        <tr
                          key={`missing-${row.seq}-${index}`}
                          ref={
                            row.first
                              ? (el) => {
                                  gapRowRefs.current[row.gap] = el
                                }
                              : undefined
                          }
                          className={`text-red-700 border-l-4 border-l-red-600 transition-colors ${
                            flashedGap === row.gap ? 'bg-red-300' : hoveredGap === row.gap ? 'bg-red-200' : 'bg-red-100'
                          }`}
                          aria-label={`Missing message, sequence ${row.seq}`}
                        >
                          <td className="px-4 py-1.5 tabular-nums font-bold">{row.seq}</td>
                          <td className="px-4 py-1.5 tabular-nums">—</td>
                          <td className="px-4 py-1.5 font-bold uppercase tracking-wide">missing</td>
                        </tr>
                      )
                    }
                    if (row.kind === 'missing-range') {
                      // Hundreds of one-per-number rows would dwarf the day's
                      // real traffic, so a wide gap gets a single line.
                      return (
                        <tr
                          key={`missing-range-${row.from}-${row.to}-${index}`}
                          ref={(el) => {
                            gapRowRefs.current[row.gap] = el
                          }}
                          className={`text-red-700 border-l-4 border-l-red-600 transition-colors ${
                            flashedGap === row.gap ? 'bg-red-300' : hoveredGap === row.gap ? 'bg-red-200' : 'bg-red-100'
                          }`}
                          aria-label={`${row.count} missing messages, sequences ${row.from} to ${row.to}`}
                        >
                          <td className="px-4 py-1.5 tabular-nums font-bold">
                            {row.from}–{row.to}
                          </td>
                          <td className="px-4 py-1.5 tabular-nums">—</td>
                          <td className="px-4 py-1.5 font-bold uppercase tracking-wide">
                            {row.count.toLocaleString()} missing
                          </td>
                        </tr>
                      )
                    }
                    const msg = row.message
                    return (
                      // Messages that arrived are left plain. They used to carry
                      // a pale red tint either side of a gap, back when nothing
                      // marked the gap itself; now that the hole has its own
                      // row, tinting the messages that did arrive only muddies
                      // what red means here.
                      <tr key={`${msg.seq}-${msg.at}-${index}`}>
                        <td className="px-4 py-1.5 tabular-nums text-gray-900">{msg.seq}</td>
                        <td className="px-4 py-1.5 tabular-nums text-gray-600">{timeOf(msg.at)}</td>
                        <td className="px-4 py-1.5 text-gray-600">{msg.type}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
